"use client";

import { useGuides, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { useEffect, useMemo, useState } from "react";
import { ChevronRightIcon, CouponIcon, DatabaseIcon, HelpCircleIcon, TokenIcon, XIcon } from "../fun-ui/icons";
import { Progress } from "@/components/ui/progress"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatTime } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button, buttonVariants } from "../fun-ui/Button";
import { Tip } from "../fun-ui/Tip";
import Image from "next/image";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CouponItem } from "@/stores/slices/userSlice";

const toPercentage = (storage: {
  used: number;
  total: number;
}) => {
  const { used = 0, total = 1 } = storage || {};
  return (used / total * 100);
}

export function UsageQuotasForSetting({
  className
}: React.HTMLAttributes<HTMLDivElement>) {
  const {
    userQuotas,
    refreshUserInfo,
    getCouponList,
  } = useUser();

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  const {
    vip,
    vipExtra,
  } = useUserVip();
  const {
    setPaymentOpen,
    setPaymentType,
  } = usePayment();


  const [translateCouponList, setTranslateCouponList] = useState<CouponItem[]>([]);
  const [summaryCouponList, setSummaryCouponList] = useState<CouponItem[]>([]);

  const translateCoupon = useMemo(() => ({
    unused: translateCouponList.filter((item) => (
      item.status === 'unused'
    )).length,
    total: translateCouponList.length
  }), [translateCouponList]);

  const summaryCoupon = useMemo(() => ({
    unused: summaryCouponList.filter((item) => (
      item.status === 'unused'
    )).length,
    total: summaryCouponList.length
  }), [summaryCouponList]);

  useEffect(() => {
    (async () => {
      const translateCouponList = await getCouponList({
        scene: 'paper_translate',
        status: '',
      });
      const summaryCouponList = await getCouponList({
        scene: 'summary',
        status: '',
      });
      setTranslateCouponList(translateCouponList);
      setSummaryCouponList(summaryCouponList);
    })();
  }, [getCouponList]);

  return (
    <div className={cn(
      "flex flex-wrap gap-5",
      className,
    )}>
      <div className="border border-border bg-[#FBFDFF] rounded-2xl min-h-[100px] flex-1 py-3.5 px-5 min-w-[200px] max-w-[250px]">
        <div className="flex justify-between text-sm whitespace-nowrap">
          <div className="flex space-x-2">
            <TokenIcon size={18} />
            <span className="text-muted-foreground whitespace-nowrap">当前飞豆</span>
          </div>
          <Link href="/balances">明细 &gt;</Link>
        </div>
        <div className="text-3xl mt-3">
          {
            userQuotas?.balance ?? ''
          }
        </div>
      </div>
      <div className="border border-border bg-[#FBFDFF] rounded-2xl min-h-[100px] flex-1 py-3.5 px-5 max-w-[250px]">
        <div className="flex justify-between text-sm">
          <div className="flex space-x-2 relative">
            <DatabaseIcon size={18} />
            <div className="flex flex-row items-center gap-2">
              <span className="text-muted-foreground whitespace-nowrap">文献存储空间</span>
              <Tip
                className="cursor-help text-annotation-foreground"
                side="top"
                label="在 Findin 可存储的最大文献数量（不含摘要型文献）"
              >
                <HelpCircleIcon />
              </Tip>
            </div>
          </div>
          {
            (vip === 0) ? (
              <div
                className="text-sm flex items-center text-primary hover:underline underline-offset-1 cursor-pointer"
                onClick={(evt) => {
                  evt.stopPropagation();
                  evt.preventDefault();
                  setPaymentType('upgrade');
                  setPaymentOpen(true);
                }}
              >
                <span>升级</span>
                <ChevronRightIcon size={12} />
              </div>
            ) : null
          }
        </div>
        {
          userQuotas?.paperStore ? (
            <div className="mt-2">
              <div className="text-2xl whitespace-nowrap">
                {userQuotas?.paperStore.used} / {userQuotas?.paperStore.total}
              </div>
              <Progress className="h-1 mt-2" value={toPercentage(userQuotas?.paperStore)} />
            </div>
          ) : ''
        }
      </div>
      {
        (vip === 1 && vipExtra?.expireTime) ? (
          <>
            <div className="border border-border bg-[#FBFDFF] rounded-2xl min-h-[100px] flex-1 py-3.5 px-5 max-w-[250px]">
              <div className="flex space-x-2 text-sm">
                <CouponIcon size={18} />
                <span className="text-muted-foreground whitespace-nowrap">Plus 会员综述减免</span>
              </div>
              <div className="text-annotation-foreground text-sm mt-0.5 whitespace-nowrap">
                {
                  vipExtra?.rightsUpdateTime ? (
                    `权益刷新时间：${formatTime(vipExtra?.rightsUpdateTime, 'YYYY-MM-DD')}`
                  ) : (
                    `权益到期时间：${formatTime(vipExtra.expireTime, 'YYYY-MM-DD')}`
                  )
                }
              </div>
              <div className="text-2xl mt-3">
                {summaryCoupon.unused} / {summaryCoupon.total}
              </div>
            </div>
            <div className="border border-border bg-[#FBFDFF] rounded-2xl min-h-[100px] flex-1 py-3.5 px-5 max-w-[250px]">
              <div className="flex space-x-2 text-sm">
                <CouponIcon size={18} />
                <span className="text-muted-foreground whitespace-nowrap">Plus 会员翻译减免</span>
              </div>
              <div className="text-annotation-foreground text-sm mt-0.5 whitespace-nowrap">
                {
                  vipExtra?.rightsUpdateTime ? (
                    `权益刷新时间：${formatTime(vipExtra?.rightsUpdateTime, 'YYYY-MM-DD')}`
                  ) : (
                    `权益到期时间：${formatTime(vipExtra.expireTime, 'YYYY-MM-DD')}`
                  )
                }
              </div>
              <div className="text-2xl mt-3">
                {translateCoupon.unused} / {translateCoupon.total}
              </div>
            </div>
          </>
        ) : null
      }
    </div>
  )
}

export function UsageQuotasForSidebar({
  className,
  iconOnly = false,
}: {
  iconOnly?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const path = usePathname();
  const {
    userQuotas,
    refreshUserInfo,
    setBalanceInsufficient,
  } = useUser();

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  useEffect(() => {
    console.log(userQuotas)
  }, [userQuotas]);

  const scene = 'Global';
  const showRechargeTip = useMemo(() => {
    if (userQuotas) {
      return userQuotas.balance < 100 && userQuotas.paymentCount === 0;
    }
    return false;
  }, [userQuotas]);

  const { guidesMap, fetchGuides, markGuidesViewed } = useGuides();
  useEffect(() => {
    (async () => {
      if (showRechargeTip) {
        await fetchGuides(scene);
      }
    })();
  }, [fetchGuides, showRechargeTip]);

  const {
    vip,
  } = useUserVip();
  const {
    setPaymentOpen,
    setPaymentType,
  } = usePayment();

  return (
    <>
      <Popover
        open={guidesMap[scene]?.guides?.type === 'recharge'}
        onOpenChange={(open) => {
          if (!open) {

          }
        }}
      >
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <PopoverTrigger>
              <TooltipTrigger asChild>
                <div className="min-h-10 relative">
                  <Link
                    className={cn(
                      className,
                      path.startsWith('/balances') && "bg-muted text-primary",
                    )}
                    href="/balances"
                  >
                    <TokenIcon size={18} />
                    {
                      iconOnly ? null : (
                        <span>{
                          userQuotas?.balance ?? ''
                        }
                        </span>
                      )
                    }
                  </Link>
                  {
                    iconOnly ? null : (
                      <Link
                        className={
                          cn(
                            buttonVariants({ variant: "outline" }),
                            "absolute w-fit rounded-full text-2xs font-bold border-primary h-fit text-primary py-1 px-2 hover:bg-primary/10 hover:text-primary right-2 top-1/2 -translate-y-1/2",
                          )
                        }
                        href="/balances?buying=true"
                      >充值</Link>
                    )
                  }
                </div>
              </TooltipTrigger>
            </PopoverTrigger>
            {
              !iconOnly ? (
                <TooltipContent side="right">
                  当前飞豆余额 {userQuotas?.balance ?? ''}
                </TooltipContent>
              ) : (
                <TooltipContent className="bg-background text-foreground border border-border bg-[#FBFDFF] rounded-md min-h-[100px] w-[260px] py-3.5 px-5 shadow-[0_4px_10px_0_rgba(0,0,0,0.1)]" side="right" sideOffset={12}>
                  <div className="flex justify-between text-sm">
                    <div className="flex space-x-2">
                      <TokenIcon size={18} />
                      <span className="text-muted-foreground">当前飞豆</span>
                    </div>
                  </div>
                  <div className="text-3xl mt-3">
                    {
                      userQuotas?.balance ?? ''
                    }
                  </div>
                </TooltipContent>
              )
            }
          </Tooltip>
        </TooltipProvider >
        {
          guidesMap[scene]?.guides?.type === 'recharge' ? (
            <PopoverContent
              side="right"
              className="w-[340px] border-none p-0 bg-transparent shadow-none relative"
              collisionPadding={{
                bottom: 20,
              }}>
              <Image
                width={340}
                height={386}
                src={guidesMap[scene]?.guides?.value?.url}
                alt=""
              />
              <XIcon
                size={20}
                className="absolute right-8 top-8 cursor-pointer text-accent-foreground/50 hover:text-accent-foreground"
                onClick={async () => {
                  await markGuidesViewed(scene);
                }}
              />
              <Button
                variant="default"
                className="absolute rounded-xl bottom-12 left-12 w-[248px]"
                onClick={async () => {
                  setBalanceInsufficient(0);
                  await markGuidesViewed(scene);
                }}
              >充值飞豆</Button>
            </PopoverContent>
          ) : null
        }
      </Popover>
      <TooltipProvider>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <div className="min-h-10 relative">
              <Link
                className={cn(
                  className,
                  path.startsWith('/settings') && "bg-muted text-primary",
                )}
                href="/settings"
              >
                <DatabaseIcon size={16} />
                {
                  iconOnly ? null : (
                    userQuotas?.paperStore ? (
                      <div className="flex-1 min-h-5">
                        <div className="flex flex-row justify-between items-center">
                          <div className="text-2xs text-muted-foreground">
                            {userQuotas?.paperStore.used} / {userQuotas?.paperStore.total}
                          </div>
                        </div>
                        <Progress className="h-1 mt-[1px]" value={toPercentage(userQuotas?.paperStore)} />
                      </div>
                    ) : ''
                  )
                }
              </Link>
              {
                (vip === 0 && !iconOnly) ? (
                  <div
                    className="text-xs flex items-center text-primary hover:underline underline-offset-1 absolute top-1.5 right-3 cursor-pointer"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      evt.preventDefault();
                      setPaymentType('upgrade');
                      setPaymentOpen(true);
                    }}
                  >
                    <span>升级</span>
                    <ChevronRightIcon size={12} />
                  </div>
                ) : null
              }
            </div>
          </TooltipTrigger>
          {
            !iconOnly ? (
              <TooltipContent side="right">
                文献存储空间 {userQuotas?.paperStore.used} / {userQuotas?.paperStore.total} 篇（不含摘要型文献）
              </TooltipContent>
            ) : (
              <TooltipContent className="bg-background text-foreground border border-border bg-[#FBFDFF] rounded-md min-h-[100px] w-[260px] py-3.5 px-5 shadow-[0_4px_10px_0_rgba(0,0,0,0.1)]" side="right" sideOffset={12}>
                <div className="flex justify-between text-sm">
                  <div className="flex space-x-2">
                    <DatabaseIcon size={18} />
                    <div className="flex flex-row items-center gap-2">
                      <span className="text-muted-foreground">文献存储空间</span>
                      <div
                        className="cursor-help text-annotation-foreground"
                        title="在 Findin 可存储的最大文献数量（不含摘要型文献）"
                      >
                        <HelpCircleIcon />
                      </div>
                    </div>
                  </div>
                </div>
                {
                  userQuotas?.paperStore ? (
                    <div className="mt-2">
                      <div className="text-2xl">
                        {userQuotas?.paperStore.used} / {userQuotas?.paperStore.total}
                      </div>
                      <Progress className="h-1 mt-2" value={toPercentage(userQuotas?.paperStore)} />
                    </div>
                  ) : ''
                }
              </TooltipContent>
            )
          }
        </Tooltip>
      </TooltipProvider >
    </>
  )
}

export function UsageQuotasForSubNav({
  className,
}: {
} & React.HTMLAttributes<HTMLDivElement>) {
  const {
    userQuotas,
    refreshUserInfo,
  } = useUser();

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  return (
    <>
      <Link
        className={cn(
          className,
        )}
        href="/balances"
      >
        <TokenIcon size={16} />
        <span>{
          userQuotas?.balance ?? ''
        }
        </span>
      </Link>
      <Link
        className={cn(
          className,
        )}
        href="/settings"
      >
        <DatabaseIcon size={16} />
        {
          userQuotas?.paperStore ? (
            <div className="flex-1 min-h-5">
              <div className="text-2xs">
                {userQuotas?.paperStore.used} / {userQuotas?.paperStore.total}
              </div>
              <Progress className="h-1 mt-[1px]" value={toPercentage(userQuotas?.paperStore)} />
            </div>
          ) : ''
        }
      </Link>
    </>
  )
}

export function UsageQuotasForBalances() {
  const {
    userQuotas,
    refreshUserInfo,
  } = useUser();

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  return (
    <div className="flex items-center space-x-3 min-h-9">
      <TokenIcon size={24} />
      <div className="text-3xl">
        {
          userQuotas?.balance ?? ''
        }
      </div>
    </div>
  )
}